import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { type FunctionComponent, useCallback } from "react";

import { BTLoginTypes, type MediaType } from "types/enum";

import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { areContextItemsPresent } from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu.utility";
import MenuItemDownload from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemDownload";
import MenuItemDraw from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemDraw";
import MenuItemEditOnline from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemEditOnline";
import MenuItemNotifyOwners from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemNotifyOwners";
import MenuItemNotifySubs from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemNotifySubs";
import MenuItemOpenInNewTab from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemOpenInNewTab";
import MenuItemRemoveAttachment from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemRemoveAttachment";
import MenuItemRename from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemRename";
import MenuItemSetAsMainPhoto from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemSetAsMainPhoto";
import MenuItemShowOwners from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemShowOwners";
import MenuItemShowSubs from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuItemShowSubs";
import MenuNoActions from "commonComponents/entity/media/common/MediaContextMenu/MenuItems/MenuNoActions";
import { ContextMenuOverlay } from "commonComponents/utilities/ContextMenuOverlay/ContextMenuOverlay";

import { AnnotationType } from "entity/media/common/mediaTypes";

export type AttachmentItems =
    | "download"
    | "openInNewTab"
    | "showOwners"
    | "notifyOwners"
    | "showSubs"
    | "notifySubs"
    | "draw"
    | "editOnline"
    | "trash"
    | "setAsMainPhoto"
    | "rename";

export interface IAttachmentOverlayProps {
    file: BTFileSystem;
    items: Record<AttachmentItems, boolean>;
    isReadOnly?: boolean;
    onClickDownloadVideo: (file: BTFileSystem) => Promise<void>;
    onFilePermissionsUpdate?: (file: BTFileSystem, loginType: BTLoginTypes) => Promise<void>;
    onClickNotify?: (file: BTFileSystem, loginType: BTLoginTypes) => Promise<void>;
    onClickDraw?: (
        docInstanceId: number,
        annoationType: AnnotationType,
        mediaType: MediaType,
    ) => Promise<void>;
    onClickEditOnline?: (docInstanceId: number, mediaType: MediaType) => Promise<void>;
    onClickDelete?: (file: BTFileSystem) => Promise<void>;
    onMenuClick?: () => void;
    onClickSetMainPhoto?: (file: BTFileSystem) => Promise<void>;
    onClickRename?: (file: BTFileSystem) => void;
}

export const AttachmentOverlay: FunctionComponent<IAttachmentOverlayProps> = ({
    file,
    items,
    isReadOnly,
    onClickDownloadVideo,
    onFilePermissionsUpdate,
    onClickNotify,
    onClickDraw,
    onClickEditOnline,
    onClickDelete,
    onMenuClick,
    onClickSetMainPhoto,
    onClickRename,
    ...antProps
}) => {
    const handleClickDownloadVideo = useCallback(async () => {
        await onClickDownloadVideo(file);
    }, [onClickDownloadVideo, file]);

    const handleToggleOwnerVisibility = useCallback(async () => {
        if (!onFilePermissionsUpdate) {
            return;
        }
        await onFilePermissionsUpdate(file, BTLoginTypes.CONTACT);
    }, [onFilePermissionsUpdate, file]);

    const handleToggleSubsVisibility = useCallback(async () => {
        if (!onFilePermissionsUpdate) {
            return;
        }
        await onFilePermissionsUpdate(file, BTLoginTypes.SUBS);
    }, [onFilePermissionsUpdate, file]);

    const handleNotifyOwners = useCallback(async () => {
        if (!onClickNotify) {
            return;
        }
        await onClickNotify(file, BTLoginTypes.CONTACT);
    }, [onClickNotify, file]);

    const handleNotifySubs = useCallback(async () => {
        if (!onClickNotify) {
            return;
        }
        await onClickNotify(file, BTLoginTypes.SUBS);
    }, [onClickNotify, file]);

    const handleClickDraw = useCallback(async () => {
        if (!onClickDraw) {
            return;
        }
        let annotationType = AnnotationType.PhotoDraw;
        if (file.entityId === 0) {
            annotationType = AnnotationType.TempFile;
        }
        await onClickDraw(file.id, annotationType, file.mediaType);
    }, [onClickDraw, file.entityId, file.id, file.mediaType]);

    const handleClickEditOnline = useCallback(async () => {
        if (!onClickEditOnline) {
            return;
        }
        await onClickEditOnline(file.id, file.mediaType);
    }, [onClickEditOnline, file]);

    const handleClickDelete = useCallback(async () => {
        if (!onClickDelete) {
            return;
        }
        await onClickDelete(file);
    }, [onClickDelete, file]);

    const handleClickSetMainPhoto = useCallback(async () => {
        if (!onClickSetMainPhoto) {
            return;
        }
        await onClickSetMainPhoto(file);
    }, [onClickSetMainPhoto, file]);

    const handleClickRename = useCallback(() => {
        onClickRename?.(file);
    }, [onClickRename, file]);

    const hasContextItems = areContextItemsPresent(items);
    if (!hasContextItems) {
        return <MenuNoActions {...antProps} />;
    }

    const {
        download,
        openInNewTab,
        showOwners,
        notifyOwners,
        showSubs,
        notifySubs,
        draw,
        editOnline,
        setAsMainPhoto,
        trash,
        rename,
    } = items;
    return (
        <ContextMenuOverlay>
            <BTMenu
                {...antProps}
                key={`attachment-menu-${file.id}`}
                testid={`attachment-menu-${file.id}`}
                onClick={onMenuClick}
            >
                {openInNewTab && (
                    <MenuItemOpenInNewTab key={`open-in-new-tab-${file.id}`} file={file} />
                )}
                {download && (
                    <MenuItemDownload
                        key={`download-${file.id}`}
                        file={file}
                        onClick={handleClickDownloadVideo}
                    />
                )}
                {showOwners && (
                    <MenuItemShowOwners
                        key={`show-owners-${file.id}`}
                        fileId={file.id}
                        isVisibleToOwners={file.viewingPermissions!.showOwner}
                        onClick={handleToggleOwnerVisibility}
                    />
                )}
                {notifyOwners && (
                    <MenuItemNotifyOwners
                        key={`notify-owners-${file.id}`}
                        fileId={file.id}
                        onClick={handleNotifyOwners}
                    />
                )}
                {showSubs && (
                    <MenuItemShowSubs
                        key={`show-subs-${file.id}`}
                        fileId={file.id}
                        isVisibleToSubs={file.viewingPermissions!.showSubs}
                        onClick={handleToggleSubsVisibility}
                    />
                )}
                {notifySubs && (
                    <MenuItemNotifySubs
                        key={`notify-subs-${file.id}`}
                        fileId={file.id}
                        onClick={handleNotifySubs}
                    />
                )}
                {draw && (
                    <MenuItemDraw
                        key={`draw-${file.id}`}
                        fileId={file.id}
                        onClick={handleClickDraw}
                    />
                )}
                {editOnline && (
                    <MenuItemEditOnline
                        key={`edit-online-${file.id}`}
                        fileId={file.id}
                        onClick={handleClickEditOnline}
                    />
                )}
                {rename && onClickRename && (
                    <MenuItemRename
                        key={`rename-${file.id}`}
                        fileId={file.id}
                        onClick={handleClickRename}
                    />
                )}
                {setAsMainPhoto && (
                    <MenuItemSetAsMainPhoto
                        key={`set-as-main-photo-${file.id}`}
                        fileId={file.id}
                        onClick={handleClickSetMainPhoto}
                    />
                )}
                {trash && (
                    <MenuItemRemoveAttachment
                        key={`remove-attachment-${file.id}`}
                        fileId={file.id}
                        isTempFile={file.isTempFile}
                        onClick={handleClickDelete}
                    />
                )}
            </BTMenu>
        </ContextMenuOverlay>
    );
};
